import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Spinner } from 'react-bootstrap'
import axios from '../axios'
import { Confetti, WinnersCarousel } from '../components/WinnersCarousel'

const STATUS_META = {
    active:    { label: '● LIVE',    bg: '#dc2626', color: '#fff', pulse: true },
    upcoming:  { label: 'Upcoming',  bg: '#f97316', color: '#fff', pulse: false },
    completed: { label: 'Completed', bg: '#1f2937', color: '#fff', pulse: false },
    cancelled: { label: 'Cancelled', bg: '#6b7280', color: '#fff', pulse: false },
    draft:     { label: 'Draft',     bg: '#f3f4f6', color: '#9ca3af', pulse: false },
}

function StatusBadge({ status }) {
    const m = STATUS_META[status] || STATUS_META.draft
    return (
        <span style={{
            background: m.bg, color: m.color,
            fontSize: '0.6rem', fontWeight: 900,
            padding: '0.15rem 0.5rem', borderRadius: 4,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            animation: m.pulse ? 'livePulse 1.5s ease-in-out infinite' : 'none',
            flexShrink: 0,
        }}>{m.label}</span>
    )
}

function TournamentCard({ t }) {
    const navigate = useNavigate()
    const { userInfo } = useSelector(state => state.userLogin)
    const [regStatus, setRegStatus] = useState(null)
    const [regMessage, setRegMessage] = useState('')
    const [hovered, setHovered]       = useState(false)

    const dateStr = t.start_date
        ? new Date(t.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : null
    const regCutoffStr = t.start_date
        ? (() => {
            const d = new Date(t.start_date + 'T00:00:00')
            d.setDate(d.getDate() - 14)
            return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
          })()
        : null
    const isPast     = t.status === 'completed' || t.status === 'cancelled'
    const isUpcoming = t.status === 'active'    || t.status === 'upcoming'

    // Enrich highlights with round, course, yardage info and normalize structure for WinnersCarousel
    const enrichedHighlights = React.useMemo(() => {
        if (!t.highlights) return []
        
        // Build course lookup map
        const courseMap = {}
        if (t.days) {
            t.days.forEach(day => {
                if (day.course_id) {
                    courseMap[day.course_id] = {
                        name: day.course,
                        par: day.course_par,
                        yardage: day.course_yardage
                    }
                }
            })
        }

        return t.highlights.map(h => {
            const enriched = { ...h }
            // Note: kind comes from API (already set to 'contest' or 'award')
            
            // For contests: enrich with round, course, yardage
            if (enriched.kind === 'contest' && t.contests && h.contest_id) {
                const contest = t.contests.find(c => c.id === h.contest_id)
                if (contest) {
                    enriched.contest_name = contest.name
                    
                    // Find matching round for course details
                    if (contest.rounds && h.round_id) {
                        const round = contest.rounds.find(r => r.id === h.round_id)
                        if (round) {
                            enriched.round = round.round
                            enriched.course_name = courseMap[round.course_id]?.name || courseMap[contest.rounds[0]?.course_id]?.name
                            enriched.yardage = courseMap[round.course_id]?.yardage || courseMap[contest.rounds[0]?.course_id]?.yardage
                        }
                    }
                }
            }
            
            return enriched
        })
    }, [t.highlights, t.contests, t.days])

    const handleRegister = async (e) => {
        e.stopPropagation()
        if (!userInfo) { navigate('/login'); return }
        setRegStatus('loading')
        try {
            await axios.post('/api/tournaments/register/',
                { tournament_id: t.id },
                { headers: { Authorization: `Bearer ${userInfo.token}` } }
            )
            setRegStatus('success'); setRegMessage("You're registered!")
        } catch (err) {
            const detail = err.response?.data?.detail || ''
            if (err.response?.status === 409 || detail.toLowerCase().includes('already')) {
                setRegStatus('already'); setRegMessage('Already registered')
            } else {
                setRegStatus('error'); setRegMessage(detail || 'Registration failed — try again')
            }
        }
    }

    return (
        <div
            onClick={() => navigate(`/events/${t.id}`)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                position: 'relative',
                background: '#fff',
                border: '1.5px solid var(--bnb-border)',
                borderRadius: 14,
                overflow: 'hidden',
                opacity: 1,
                boxShadow: isPast ? 'none' : '0 2px 12px rgba(0,48,135,0.07)',
                marginBottom: '0.65rem',
                cursor: 'pointer',
                transition: 'box-shadow 0.15s',
            }}
        >
            {/* Banner image — overlays */}
            {t.image_url && (
                <div className="bnb-event-card-image" >
                    <img src={t.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', mixBlendMode: 'multiply' }}  />

                    {/* Top-right: registration deadline (upcoming only) */}
                    {isUpcoming && regCutoffStr && (
                        <div className="bnb-event-registration-overlay" style={{
                            position: 'absolute', top: 0, right: 0, bottom: 0,
                            display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center',
                            padding: '0 1.25rem',
                            textAlign: 'right',
                            pointerEvents: 'none',
                        }}>
                            <div style={{ fontSize: '1.85rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#fff', lineHeight: 1.2, textShadow: '0 2px 8px rgba(0,0,0,0.7)', whiteSpace: 'nowrap' }}>
                                Registration Ends
                            </div>
                            <div style={{ fontSize: '1.65rem', fontWeight: 900, color: '#fff', lineHeight: 1.2, textShadow: '0 2px 12px rgba(0,0,0,0.75)', whiteSpace: 'nowrap' }}>
                                {regCutoffStr}
                            </div>
                            <div style={{ marginTop: '0.75rem' }} onClick={e => e.stopPropagation()}>
                                {regStatus === 'success' || regStatus === 'already' ? (
                                    <span style={{ fontSize: '0.8rem', color: '#fff', fontWeight: 700, textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
                                        <i className="fa-solid fa-circle-check me-1"></i>{regMessage}
                                    </span>
                                ) : (
                                    <>
                                        <button
                                            onClick={handleRegister}
                                            disabled={regStatus === 'loading'}
                                            style={{
                                                padding: '0.5rem 1.4rem', borderRadius: 7,
                                                background: 'var(--bnb-navy)', color: '#fff',
                                                border: '2px solid rgba(255,255,255,0.3)',
                                                fontWeight: 800, fontSize: '0.85rem',
                                                cursor: regStatus === 'loading' ? 'not-allowed' : 'pointer',
                                                opacity: regStatus === 'loading' ? 0.7 : 1,
                                                boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
                                            }}
                                        >
                                            {regStatus === 'loading' ? 'Registering…' : userInfo ? 'Register' : 'Login to Register'}
                                        </button>
                                        {regStatus === 'error' && <div style={{ fontSize: '0.7rem', color: '#fca5a5', marginTop: '0.3rem', textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>{regMessage}</div>}
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Top-left: logo + name + status + date + location */}
                    <div style={{
                        position: 'absolute', top: '0.75rem', left: '0.75rem',
                        display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                        background: 'rgba(0,0,0,0.58)',
                        backdropFilter: 'blur(8px)',
                        borderRadius: 12,
                        padding: '0.75rem 1rem',
                        maxWidth: 320,
                    }}>
                        {t.logo_url && (
                            <div style={{ width: 52, height: 52, flexShrink: 0, borderRadius: 8, overflow: 'hidden', background: '#fff', marginTop: 1 }}>
                                <img src={t.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                                <span style={{ fontSize: '1rem', fontWeight: 800, color: '#fff', lineHeight: 1.2, textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
                                    {t.name}
                                </span>
                                <StatusBadge status={t.status} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                                {dateStr && (
                                    <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                        <i className="fa-solid fa-calendar"></i>{dateStr}
                                    </span>
                                )}
                                {t.location && (
                                    <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                        <i className="fa-solid fa-location-dot"></i>{t.location}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {isPast && (
                        <div className="bnb-winners-carousel-mobile">
                            <div className="bnb-wc-confetti-layer"><Confetti /></div>
                            <WinnersCarousel winnerDetails={t.winner_details || []} highlights={enrichedHighlights} />
                        </div>
                    )}
                </div>
            )}

            {/* Hover overlay */}
            <div style={{
                position: 'absolute', inset: 0,
                background: 'rgba(0,0,0,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: hovered ? 1 : 0,
                transition: 'opacity 0.2s ease',
                pointerEvents: 'none',
                zIndex: 30,
            }}>
                <span style={{ color: '#fff', fontSize: '2.8rem', fontWeight: 900, letterSpacing: '0.15em', textShadow: '0 2px 14px rgba(0,0,0,0.55)', textTransform: 'uppercase' }}>
                    View
                </span>
            </div>

        </div>
    )
}

function EventsScreen() {
    const [tournaments, setTournaments] = useState([])
    const [loading, setLoading]         = useState(true)
    const [error, setError]             = useState(null)

    useEffect(() => {
        axios.get('/api/tournaments/public/')
            .then(res => { setTournaments(res.data); setLoading(false) })
            .catch(err => { setError(err.response?.data?.detail || err.message); setLoading(false) })
    }, [])

    const active   = tournaments.filter(t => t.status === 'active' || t.status === 'upcoming')
    const archived = tournaments.filter(t => t.status === 'completed' || t.status === 'cancelled')

    return (
        <div style={{ paddingTop: '0.5rem', maxWidth: 1400, margin: '0 auto' }}>

            {/* Hero */}
            <div className="bnb-hero mb-4" style={{ paddingBottom: '1.5rem' }}>
                <div className="bnb-hero-eyebrow">
                    <i className="fa-solid fa-calendar-days me-2"></i>Tournament Schedule
                </div>
                <h1 className="bnb-hero-title">Events</h1>
                <p className="bnb-hero-sub page-events">All official BNB Golf Club tournaments — past and present.</p>
            </div>

            {loading && (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <Spinner animation="border" style={{ color: 'var(--bnb-navy)' }} />
                </div>
            )}

            {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '1rem', color: '#991b1b', marginBottom: '1rem' }}>
                    {error}
                </div>
            )}

            {!loading && !error && (
                <>
                    {active.length > 0 && (
                        <div style={{ marginBottom: '1.75rem' }}>
                            <div className="bnb-section-label">Active &amp; Upcoming</div>
                            {active.map(t => <TournamentCard key={t.id} t={t} />)}
                        </div>
                    )}
                    {archived.length > 0 && (
                        <div>
                            <div className="bnb-section-label">Past Events</div>
                            {archived.map(t => <TournamentCard key={t.id} t={t} />)}
                        </div>
                    )}
                    {tournaments.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#9ca3af' }}>
                            <i className="fa-solid fa-calendar-xmark" style={{ fontSize: '2.5rem', color: '#d1d5db', display: 'block', marginBottom: '1rem' }}></i>
                            No tournaments yet.
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default EventsScreen
